import z from "zod";
import { ProductItemSchema } from "../product";
import { OrderStatus, OrderType } from "./order";
import { PaymentResponseSchema } from "../payment";
import { PersonPickupFormSchema, PersonResponseSchema } from "../person";
import { AddressAdminResponseSchema } from "../address";
import { CourierSchema } from "../courier";

const OrderItemResponseBaseSchema = z.object({
  id: z.number().optional(),
  product_item: ProductItemSchema,
  unit_price: z.number().nullish(),
  quantity: z
    .number()
    .int({ message: "validation.order.quantity_integer" })
    .min(1),
});

const OrderResponseBaseSchema = z.object({
  id: z.string(),
  status: z.nativeEnum(OrderStatus).optional(),
  order_items: OrderItemResponseBaseSchema.array(),
  created_at: z.string(),
  updated_at: z.string(),

  payment: PaymentResponseSchema.array(),
  preffered_time: z.string().nullish(),
  preffered_date: z.string().nullish(),
  comment: z.string().nullish(),
});

const OrderDeliveryResponseSchema = OrderResponseBaseSchema.extend({
  type: z.literal(OrderType.DELIVERY),
  person: PersonResponseSchema,
  courier: CourierSchema.nullish(),
});

const OrderPickupResponseSchema = OrderResponseBaseSchema.extend({
  type: z.literal(OrderType.PICKUP),
  person: PersonPickupFormSchema,
});

const OptionalAdminEmailSchema = z
  .union([
    z.literal(""),
    z.string().email({ message: "validation.field.invalidEmail" }),
  ])
  .nullish();

const AddressDeliveryAdminResponseSchema = AddressAdminResponseSchema.extend({
  postal_code: z.string().nullish(),
  city: z.string().nullish(),
});

const PersonDeliveryAdminResponseSchema = PersonResponseSchema.extend({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: OptionalAdminEmailSchema,
  address: AddressDeliveryAdminResponseSchema,
});

const PersonPickupAdminResponseSchema = PersonPickupFormSchema.extend({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: OptionalAdminEmailSchema,
});

export const OrderResponseSchema = z.discriminatedUnion("type", [
  OrderDeliveryResponseSchema,
  OrderPickupResponseSchema,
]);

export type OrderResponse = z.infer<typeof OrderResponseSchema>;

export const CheckoutAttemptResponseSchema = z.object({
  checkout_attempt_id: z.string().uuid(),
  order: OrderResponseSchema,
});

export type CheckoutAttemptResponse = z.infer<
  typeof CheckoutAttemptResponseSchema
>;

export type OrderDeliveryResponse = z.infer<typeof OrderDeliveryResponseSchema>;
export type OrderPickupResponse = z.infer<typeof OrderPickupResponseSchema>;

const OrderDeliveryAdminResponseSchema = OrderResponseBaseSchema.extend({
  type: z.literal(OrderType.DELIVERY),
  person: PersonDeliveryAdminResponseSchema,
  courier: CourierSchema.nullish(),
});

const OrderPickupAdminResponseSchema = OrderResponseBaseSchema.extend({
  type: z.literal(OrderType.PICKUP),
  person: PersonPickupAdminResponseSchema,
});

export const OrderAdminResponseSchema = z.discriminatedUnion("type", [
  OrderDeliveryAdminResponseSchema,
  OrderPickupAdminResponseSchema,
]);

export type OrderAdminResponse = z.infer<typeof OrderAdminResponseSchema>;

export type OrderDeliveryAdminResponse = z.infer<
  typeof OrderDeliveryAdminResponseSchema
>;
export type OrderPickupAdminResponse = z.infer<
  typeof OrderPickupAdminResponseSchema
>;
